<template>
  <div v-if="mode==='order'" :class="{'disabled': disabled, 'readonly': readonly}">
    <draggable v-model="inputVal" draggable=".draggable-item" @change="onMoved">
      <div v-for="(datum, datumIndex) in inputVal" :key="datumIndex" class="draggable-item">
        {{ datum.text }}
        <i class="fa fa-arrows float-right" aria-hidden="true" />
      </div>
    </draggable>
    <div class="clear" />
    <button class="btn btn-light float-right set-mode" @click="cancelChanged()">
      {{ $t('common.button.cancel') }}
    </button>
    <button class="btn btn-light float-right set-mode" @click="setMode('normal')">
      {{ $t('common.button.save') }}
    </button>
    <div class="clear" />
  </div>
  <div v-else-if="orderable" :class="{'disabled': disabled, 'readonly': readonly}">
    <b-input-group>
      <multiselect
        :id="id"
        v-model="inputVal"
        :options="optionsInternal"
        :multiple="multiple"
        label="text"
        :select-label="selectLabel"
        :select-group-label="selectGroupLabel"
        :deselect-label="deselectLabel"
        :deselect-group-label="deselectGroupLabel"
        :tag-placeholder="tagPlaceholder"
        :selected-label="selectedLabel"
        track-by="value"
        :disabled="readonly || disabled"
        :placeholder="placeholder"
        class="form-control"
        :name="name"
        @input="multiselectInput"
        @search-change="asyncSearch">
        <template slot="noResult">{{ $t('common.notification.no_data') }}</template>
        <template slot="noOptions">{{ $t('common.notification.no_data') }}</template>
      </multiselect>
      <b-input-group-append v-if="value && value.length > 0 && !readonly && !disabled">
        <b-button @click="removeValue">
          X
        </b-button>
      </b-input-group-append>
    </b-input-group>

    <div class="clear" />
    <button v-if="inputVal && inputVal.length > 1 && !readonly && !disabled" class="btn btn-light float-right set-mode" @click="setMode('order')">
      {{ $t('common.button.change_ordr') }}
    </button>
    <div class="clear" />
  </div>
  <div v-else :class="{'disabled': disabled, 'readonly': readonly}">
    <b-input-group>
      <multiselect
        :id="id"
        v-model="inputVal"
        :options="optionsInternal"
        :multiple="multiple"
        label="text"
        :select-label="selectLabel"
        :select-group-label="selectGroupLabel"
        :deselect-label="deselectLabel"
        :deselect-group-label="deselectGroupLabel"
        :tag-placeholder="tagPlaceholder"
        :selected-label="selectedLabel"
        track-by="value"
        :disabled="readonly || disabled"
        :placeholder="placeholder"
        class="form-control"
        :name="name"
        @input="multiselectInput"
        @search-change="asyncSearch">
        <template slot="noResult">{{ $t('common.notification.no_data') }}</template>
        <template slot="noOptions">{{ $t('common.notification.no_data') }}</template>
      </multiselect>
      <b-input-group-append v-if="value && value.length > 0 && !readonly && !disabled">
        <b-button @click="removeValue">
          X
        </b-button>
      </b-input-group-append>
    </b-input-group>
  </div>
</template>


<script>
export default {
  props: {
    value: {
      type: [String, Number, Boolean, Array, Object],
      default: undefined
    },
    options: {
      type: Array,
      default: function() {
        return []
      }
    },
    readonly: {
      type: Boolean,
      default: false
    },
    visibleData: {
      type: Object,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    selectLabel: {
      type: String,
      default: ''
    },
    selectGroupLabel: {
      type: String,
      default: ''
    },
    deselectLabel: {
      type: String,
      default: ''
    },
    deselectGroupLabel: {
      type: String,
      default: ''
    },
    tagPlaceholder: {
      type: String,
      default: ''
    },
    selectedLabel: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: true
    },
    id: {
      type: String,
      default: ''
    },
    orderable: {
      type: Boolean,
      default: false
    },
    asyncSearchUrl: {
      type: String,
      default: undefined,
      required: false
    },
    searchQueryName: {
      type: String,
      default: 'q'
    },
    asyncSearchFilter: {
      type: Function,
      default: undefined
    }
  },
  data() {
    return {
      inputVal: null,
      mode: 'normal',
      optionsInternal: []
    }
  },
  watch: {
    value: function(newVal) {
      this.inputVal = newVal
    },
    options: {
      handler: function(newVal) {
        this.optionsInternal = this.options
      },
      deep: true
    }
  },
  mounted() {
    this.optionsInternal = this.options
    this.inputVal = this.value
  },
  methods: {
    removeValue() {
      this.inputVal = null
      this.$emit('input', this.inputVal)
      this.$emit('change', this.inputVal)
    },
    setMode(mode) {
      if (mode === 'order') {
        this.originalVal = this.inputVal
      }
      this.mode = mode
    },
    multiselectInput(val) {
      this.$emit('input', val)
      this.$emit('change', val)
    },
    cancelChanged() {
      this.inputVal = this.originalVal
      this.$emit('input', this.inputVal)
      this.$emit('change', this.inputVal)
      this.setMode('normal')
    },
    onMoved() {
      this.$emit('input', this.inputVal)
      this.$emit('change', this.inputVal)
    },
    asyncSearch(query) {
      if (!this.asyncSearchUrl || !query) {
        return
      }
      if (this.asyncSearchTimer) {
        clearTimeout(this.asyncSearchTimer)
        delete this.asyncSearchTimer
      }
      this.asyncSearchTimer = setTimeout(() => {
        if (this.asyncSearchTimer) {
          delete this.asyncSearchTimer
        }
        this.search(query)
      }, 500)
    },
    search(query) {
      let url = this.asyncSearchUrl
      const successHandler = async response => {
        if (this.asyncSearchFilter) {
          response = await this.asyncSearchFilter(response, query)
        }
        if (response.resultCode === 'success') {
          this.optionsInternal = response.data
        }
      }
      const failHandler = error => {
        if (error.response && error.response.data) {
          console.error(this.$t(error.response.data.message))
          this.$toast.error(this.$t(error.response.data.message))
        } else {
          console.error(error)
          this.$toast.error(error)
        }
      }
      if (this.asyncSearchUrl.endsWith('WithData')) {
        const data = {
          q: query,
          data: this.visibleData
        }
        this.$axios
          .$post(url, data)
          .then(successHandler)
          .catch(failHandler)
      } else {
        if (this.asyncSearchUrl.indexOf('?') >= 0) {
          url += '&'
        } else {
          url += '?'
        }
        url += this.searchQueryName + '=' + encodeURIComponent(query)

        const commonHeaders = this.$axios.defaults.headers.common
        let auth
        let refreshToken
        if (url.startsWith('https://dapi.kakao.com')) {
          auth = commonHeaders.Authorization
          refreshToken = commonHeaders['Refresh-Token']
          commonHeaders.Authorization =
            'KakaoAK ' + this.configs.kakao_map_api_key
          delete commonHeaders['Refresh-Token']
        } else if (url.startsWith('https://maps.googleapis.com')) {
          auth = commonHeaders.Authorization
          refreshToken = commonHeaders['Refresh-Token']
          delete commonHeaders.Authorization
          delete commonHeaders['Refresh-Token']
        }

        this.$axios
          .$get(url, { no_loading: true })
          .then(successHandler)
          .catch(failHandler)

        if (auth) {
          commonHeaders.Authorization = auth
        }
        if (refreshToken) {
          commonHeaders['Refresh-Token'] = refreshToken
        }
      }
    }
  }
}
</script>

<style scoped>
.draggable-item {
  cursor: move;
  border: 1px solid #dddddd;
  border-radius: 5px;
  padding: 5px;
  margin-right: -2px;
}
.fa-arrows {
  margin: 4px;
}
button.set-mode {
  border-radius: 0 0 5px 5px;
  margin-right: -2px;
  margin-top: -2px;
  border: 2px solid #e4e7ea;
  padding: 0.175rem 0.5rem;
  font-size: 0.775rem;
}
>>> ::placeholder {
  color: #adadad;
}
>>> .multiselect {
  box-sizing: border-box;
  padding: 0;
}
>>> .multiselect__tags {
  border: none;
  background: transparent;
}
>>> .multiselect__placeholder {
  margin-left: 5px;
}
>>> input[type='text'] {
  margin-top: 3px;
  padding-bottom: 2px;
}
.readonly >>> .multiselect__tag,
.disabled >>> .multiselect__tag {
  padding: 4px 10px;
}
.readonly >>> .multiselect__tag > i,
.disabled >>> .multiselect__tag > i {
  display: none !important;
}

>>> .multiselect__input,
>>> .multiselect__single {
  margin-top: 0;
  margin-bottom: 0;
}
>>> .multiselect__single {
  white-space: nowrap;
}
>>> .multiselect__placeholder {
  margin-bottom: 0;
}
>>> .multiselect__tags {
  padding-top: 5px;
  min-height: 32px;
  font-size: 0.75rem !important;
}
>>> .multiselect__select {
  height: 31px;
}
>>> input[type='text'] {
  margin-top: 1px;
}
>>> .multiselect__input,
>>> .multiselect__single {
  background: transparent;
}
>>> .multiselect {
  min-height: 32px;
  height: auto;
}
</style>
